import { addDays, format, formatISO } from "date-fns";

import { getStorage } from "store/local";
import { findActiveModules } from "tools/helpers";
import { layout as layoutReport } from "./AccessDailyWizard";
import proxy from "api/proxy";
import ButtonEvents from "./ButtonEvents";
import ShiftHoursInfo from "./ShiftHoursInfo";
import { getAccessEditLimit, isAccessWithinLimit } from "./accessLimit";

let accessEditLimit = 0;

const tagColorsState = {
  open: "amber",
  done: "sky",
  close: "neutral",
};

const tagColorsApproved = {
  yes: "bg-lime-300 text-lime-900",
  no: "bg-neutral-600 text-neutral-100",
};

function getFilters() {
  return {
    extras: [
      "OR",
      [["heno", ">", 0]],
      [["hedo", ">", 0]],
      [["hedf", ">", 0]],
      [["henf", ">", 0]],
    ],
    close: [
      ["state", "=", "close"],
      // ["booking_date", ">=", yesterday],
      // ["shop", "=", session.shop],
    ],
    all: [
      // ["state", "=", "close"],
      // ["booking_date", ">=", yesterday],
      // ["shop", "=", session.shop],
    ],
  };
}

const checkApproveVisibility = (record, allowedStates) => {
  if (!record) return false;

  const state = record.state?.id ?? record.state;

  const hasOvertime =
    Math.max(
      record.heno || 0,
      record.hedo || 0,
      record.hedf || 0,
      record.henf || 0,
    ) > 0;

  const exceededLimit = !isAccessWithinLimit(
    record.enter_timestamp,
    accessEditLimit,
  );

  return (
    record.enter_timestamp &&
    record.exit_timestamp &&
    allowedStates.includes(state) &&
    hasOvertime &&
    !exceededLimit
  );
};

function invisibleApprove(_, record) {
  return !checkApproveVisibility(record, ["close", "not_approved"]);
}

function invisibleNotApprove(_, record) {
  return !checkApproveVisibility(record, ["close", "approved"]);
}

async function getDeepDomain() {
  const session = getStorage("ctxSession");
  const { data } = await proxy.search(
    "staff.section",
    [["chiefs.id", "=", Number(session?.user_employee)]],
    ["employees", "chiefs"],
  );
  const sections = data.map((val) => val["id"]);
  return [["section", "in", sections]];
}

function getShiftHours(record) {
  const shift = record?.["shift."];
  const kind = shift?.["shift_kind."];
  if (!kind?.start || !kind?.end) return "";

  const txt = `${kind.start.slice(0, -3)} - ${kind.end.slice(0, -3)}`;
  return txt;
}

function readOnlyAccess(record, nameField) {
  const state = record?.state?.id ?? record?.state;
  if (state === "done") return true;
  const timestamp =
    nameField === "exit_timestamp" && record?.exit_timestamp
      ? record.exit_timestamp
      : record?.enter_timestamp;
  return !isAccessWithinLimit(timestamp, accessEditLimit);
}

const optionsBillable = [
  { id: "", name: "" },
  { id: "billable", name: "Facturable" },
  { id: "no_billable", name: "No Facturable" },
];

const optionsPermission = [
  { id: "", name: "" },
  { id: "late", name: "Tarde" },
  { id: "early", name: "Temprano" },
  { id: "late_justified", name: "Tarde Justificado" },
];

const STATES = [
  { id: "", name: "" },
  { id: "open", name: "Abierto" },
  { id: "close", name: "Cerrado" },
  { id: "approved", name: "Aprobado" },
  { id: "not_approved", name: "No Aprob." },
  { id: "done", name: "Finalizado" },
];

function getEnterDate(record, nameField) {
  if (record && record[nameField]) {
    return format(`${record[nameField]}Z`, "dd.MMM.yyyy");
  }
  return "";
}

function getFmtTime(record, nameField) {
  let fmtEnterTime = "";
  let enter_field = "enter_timestamp";
  let exit_field = "exit_timestamp";
  if (nameField === "bio") {
    enter_field = "enter_biometric";
    exit_field = "exit_biometric";
  }
  if (record && record[enter_field]) {
    let enter_ = record[enter_field];
    let exit_ = record[exit_field];
    if (typeof enter_ === "string") {
      enter_ = `${enter_}Z`;
    }
    fmtEnterTime = format(enter_, "hh:mm aaa > ");
    if (exit_) {
      if (typeof exit_ === "string") {
        exit_ = `${exit_}Z`;
      }
      fmtEnterTime += format(exit_, "hh:mm aaa");
    }
  }
  return fmtEnterTime;
}

async function actionApprove(active, store, method) {
  const notes = store.notes ?? active.notes;
  if (!notes) {
    return { error: { error: "El campo nota es requerido", type: "error" } };
  }

  return await proxy.buttonMethod({
    model: "staff.access",
    method: method,
    ids: [active.id],
    ctx: { notes },
  });
}

async function handleApprove(active, store) {
  return await actionApprove(active, store, "approve");
}

async function handleNotApprove(active, store) {
  return await actionApprove(active, store, "not_approve");
}

const extraDepends = ["staff_access_bill", "crm_staff"];

function onChangeDate(selected) {
  if (!selected?.fromDate) return null;
  const nextDay = selected.toDate
    ? addDays(selected.toDate, 1)
    : addDays(selected.fromDate, 1);
  return [
    "AND",
    [
      ["enter_timestamp", ">=", formatISO(selected.fromDate)],
      ["enter_timestamp", "<", formatISO(nextDay)],
    ],
  ];
}

async function layoutAccess() {
  const [res, softDomain, editLimit] = await Promise.all([
    findActiveModules(extraDepends),
    getDeepDomain(),
    getAccessEditLimit(),
  ]);
  accessEditLimit = editLimit;

  const ctxView = {
    model: "staff.access",
    form_action: ["edit"],
    table_action: ["open"],
    limit: 100,
    activeSearch: true,
    // hiddenTitle: true,
    filterDate: onChangeDate,
    filters: getFilters,
    softDomain,
    orderBy: [["enter_timestamp", "DESC"]],
    tags: {
      state: tagColorsState,
      // overtime_approved: tagColorsApproved,
    },
    toolbar_table: {
      reports: [
        {
          name: "staff.access.daily_report",
          description: "staff.access.daily_report.description",
          layout: layoutReport(),
        },
      ],
    },
    fields: {
      employee: {
        type: "many2one",
        required: true,
        model: "company.employee",
        attrs: ["contract", "contract.employment_benefits"],
        searchable: true,
        readOnly: true,
      },
      enter_date: {
        type: "date",
        function: (rec) => getEnterDate(rec, "enter_timestamp"),
      },
      fmt_time: {
        type: "date",
        function: (rec) => getFmtTime(rec),
      },
      enter_date_bio: {
        type: "date",
        function: (rec) => getEnterDate(rec, "enter_biometric"),
      },
      fmt_time_bio: {
        type: "date",
        function: (rec) => getFmtTime(rec, "bio"),
      },
      enter_timestamp: {
        type: "datetime",
        readOnly: (record) => readOnlyAccess(record, "enter_timestamp"),
        required: true,
      },
      exit_timestamp: {
        type: "datetime",
        readOnly: (record) => readOnlyAccess(record, "exit_timestamp"),
      },
      start_rest: {
        type: "datetime",
        readOnly: (record) => readOnlyAccess(record, "enter_timestamp"),
      },
      end_rest: {
        type: "datetime",
        readOnly: (record) => readOnlyAccess(record, "exit_timestamp"),
      },
      enter_biometric: {
        type: "datetime",
        readOnly: true,
      },
      exit_biometric: {
        type: "datetime",
        readOnly: true,
      },
      notes: {
        type: "text",
        readOnly: false,
      },
      ttt: {
        type: "float",
        readOnly: true,
      },
      heno: {
        type: "float",
        readOnly: true,
      },
      hedo: {
        type: "float",
        readOnly: true,
      },
      henf: {
        type: "float",
        readOnly: true,
      },
      hedf: {
        type: "float",
        readOnly: true,
      },
      het: {
        type: "float",
        readOnly: true,
      },
      reco: {
        type: "float",
        readOnly: true,
      },
      recf: {
        type: "float",
        readOnly: true,
      },
      dom: {
        type: "float",
        readOnly: true,
      },
      state: {
        type: "selection",
        readOnly: true,
        default: "open",
        options: STATES,
        tags: tagColorsState,
        // filter: true,
      },
      enter_permission: {
        type: "selection",
        options: optionsPermission,
        readOnly: (record) => readOnlyAccess(record, "enter_timestamp"),
      },
      exit_permission: {
        type: "selection",
        options: optionsPermission,
        readOnly: (record) => readOnlyAccess(record, "exit_timestamp"),
      },
      approve: {
        type: "button",
        method: handleApprove,
        color: "blue",
        style: "w-full",
        invisible: invisibleApprove,
        // onSuccessMsg: "Bloqueo exitosa!",
      },
      not_approve: {
        type: "button",
        method: handleNotApprove,
        color: "rose",
        style: "w-max!",
        invisible: invisibleNotApprove,
      },

      shift: {
        type: "many2one",
        model: "staff.shift",
        readOnly: true,
        attrs: [
          "shift_kind.start",
          "shift_kind.end",
          "shift_kind.start_rest",
          "shift_kind.end_rest",
        ],
      },
      shift_hours: {
        type: "date",
        function: getShiftHours,
      },
      shift_schedule_het: {
        type: "float",
        readOnly: true,
      },
      payroll: {
        type: "many2one",
        model: "staff.payroll",
        readOnly: true,
      },
      compensatory_date: {
        type: "date",
        readOnly: (record) => !!record?.compensated,
      },
      compensated: {
        type: "many2one",
        model: "staff.shift",
        readOnly: true,
      },
      section: {
        type: "many2one",
        model: "staff.section",
        searchable: true,
        readOnly: true,
      },
      shift_unit_cost: {
        type: "numeric",
        readOnly: true,
      },
      shift_amount: {
        type: "numeric",
        readOnly: true,
      },
      shift_amount_extra: {
        type: "numeric",
        // readOnly: true,
      },
      shift_amount_transport: {
        type: "numeric",
        // readOnly: true,
      },
      view_events: {
        type: "custom_component",
        Component: ButtonEvents,
      },
      shift_info: {
        type: "custom_component",
        Component: ShiftHoursInfo,
      },
    },
    list: [
      { name: "employee" },
      { name: "section" },
      { name: "enter_date_bio" },
      { name: "fmt_time_bio" },
      { name: "shift" },
      { name: "shift_hours" },
      { name: "shift_schedule_het" },
      { name: "het" },
      { name: "enter_date" },
      { name: "fmt_time" },
      { name: "hedo" },
      { name: "heno" },
      { name: "hedf" },
      { name: "henf" },
      { name: "reco" },
      { name: "recf" },
      { name: "dom" },

      { name: "state" },
      // { name: "enter_permission" },
      // { name: "reco" },
      // { name: "recf" },
      { name: "enter_timestamp", invisible: true },
      { name: "exit_timestamp", invisible: true },
      { name: "enter_biometric", invisible: true },
      { name: "exit_biometric", invisible: true },
    ],
    form: [
      { name: "shift_info", colSpan: "col-span-2" },
      { name: "employee", colSpan: "col-span-1" },
      {
        name: "het",
        type: "grid",
        elements: [
          { name: "shift" },
          { name: "shift_schedule_het" },
          { name: "het" },
        ],
        colSpan: "col-span-1",
        col: "grid-cols-3 md:grid-cols-3",
      },
      { name: "enter_timestamp", colSpan: "col-span-1" },
      { name: "enter_biometric", colSpan: "col-span-1" },
      { name: "start_rest", colSpan: "col-span-1" },
      { name: "end_rest", colSpan: "col-span-1" },
      { name: "exit_timestamp", colSpan: "col-span-1" },
      { name: "exit_biometric", colSpan: "col-span-1" },
      { name: "enter_permission", colSpan: "col-span-2" },
      { name: "exit_permission", colSpan: "col-span-2" },
      { name: "state", colSpan: "col-span-2" },
      { name: "section", colSpan: "col-span-2" },
      { name: "payroll", colSpan: "col-span-2" },
      { name: "compensatory_date", colSpan: "col-span-1" },
      { name: "compensated", colSpan: "col-span-1" },
      {
        name: "overtime",
        type: "grid",
        elements: [
          { name: "ttt" },
          { name: "hedo" },
          { name: "heno" },
          { name: "hedf" },
          { name: "henf" },
          { name: "reco" },
          { name: "recf" },
          { name: "dom" },
        ],
        colSpan: "col-span-2",
        col: "grid-cols-4 md:grid-cols-8",
      },
      {
        name: "amounts",
        type: "grid",
        elements: [
          { name: "shift_unit_cost" },
          { name: "shift_amount_extra" },
          { name: "shift_amount_transport" },
          { name: "shift_amount" },
        ],
        colSpan: "col-span-2",
        col: "grid-cols-2 md:grid-cols-4",
      },
      { name: "notes", colSpan: "col-span-2" },
      {
        name: "buttons",
        type: "grid",
        elements: [{ name: "approve" }, { name: "not_approve" }],
        colSpan: "col-span-2",
      },
    ],
  };

  if (res.staff_access_bill === "activated") {
    ctxView.fields.is_billable = {
      type: "selection",
      options: optionsBillable,
      readOnly: (record) => readOnlyAccess(record, "enter_timestamp"),
    };
    ctxView.fields.activity = {
      type: "many2one",
      model: "staff.event_category",
      recSearch: () => [["type_event", "=", "overtime"]],
      searchable: true,
      preloaded: true,
      readOnly: (record) => readOnlyAccess(record, "enter_timestamp"),
    };
    ctxView.form.push({ name: "is_billable" }, { name: "activity" });
    ctxView.list.push({ name: "is_billable" }, { name: "activity" });
  }
  ctxView.list.push({ name: "view_events" });

  return ctxView;
}

export { layoutAccess };
